import type { AdminQuestCompletionEvent } from '@src/dto/QuestCompletionEvents';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { formatDateTimeWithLocalZone } from '../../utils/stringHelpers';

function DetailRow({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </div>
      <div
        className={`text-sm text-white break-words ${mono ? 'font-mono' : ''}`}
      >
        {children}
      </div>
    </div>
  );
}

interface QuestEventDetailModalProps {
  isOpen: boolean;
  event: AdminQuestCompletionEvent | null;
  onClose: () => void;
}

export function QuestEventDetailModal({
  isOpen,
  event,
  onClose,
}: QuestEventDetailModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Completion event</DialogTitle>
          <DialogDescription>
            An append-only record written when a partner app reports a quest
            completion.
          </DialogDescription>
        </DialogHeader>

        {event ? (
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailRow label="Wallet" mono>
                {event.walletId}
              </DetailRow>
              <DetailRow label="Reported at">
                {formatDateTimeWithLocalZone(event.completedAt)}
              </DetailRow>
              <DetailRow label="Outcome">
                {event.outcome.replace(/_/g, ' ')}
              </DetailRow>
              {event.reason ? (
                <DetailRow label="Reason">
                  {event.reason.replace(/_/g, ' ')}
                </DetailRow>
              ) : null}
              <DetailRow label="Round">Round #{event.roundNumber}</DetailRow>
              <DetailRow label="App">
                {event.appName}
                <span className="block font-mono text-xs text-neutral-500">
                  {event.androidPackage}
                </span>
              </DetailRow>
              <DetailRow label="Quest">
                {event.questTitle}
                <span className="block font-mono text-xs text-neutral-500">
                  {event.questKey}
                </span>
              </DetailRow>
              <DetailRow label="Tier">{event.questTier}</DetailRow>
              <DetailRow label="Credential">
                {event.credentialLabel ? (
                  <>
                    {event.credentialLabel}
                    {event.credentialKeyPrefix ? (
                      <span className="block font-mono text-xs text-neutral-500">
                        {event.credentialKeyPrefix}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-neutral-500">—</span>
                )}
              </DetailRow>
              <DetailRow label="Source request ID" mono>
                {event.sourceRequestId ?? (
                  <span className="font-sans text-neutral-500">—</span>
                )}
              </DetailRow>
              <DetailRow label="Badge">
                {event.badgeTitle ?? (
                  <span className="text-neutral-500">—</span>
                )}
              </DetailRow>
              <DetailRow label="Recorded at">
                {formatDateTimeWithLocalZone(event.created)}
              </DetailRow>
            </div>

            <div className="space-y-1.5">
              <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Metadata
              </div>
              <pre className="max-h-64 overflow-auto rounded-lg border border-neutral-800 bg-neutral-900/70 p-3 text-xs text-neutral-300">
                {event.metadataJson}
              </pre>
            </div>
          </DialogBody>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
